import { Injectable, NgZone } from '@angular/core';
import { fromEvent, merge, Subscription, timer } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { AuthManagerService } from './auth-manager.service';
import { DevCycleService } from './dev-cycle.service';

@Injectable({
  providedIn: 'root',
})
export class IdleService {
  private idleTimeoutMs = 10 * 60 * 1000; // Default to 10 minutes
  private activityEvents$ = merge(
    fromEvent(window, 'mousemove'),
    fromEvent(window, 'mousedown'),
    fromEvent(window, 'keypress'),
    fromEvent(window, 'touchstart'),
  );
  private subscription: Subscription | null = null;

  constructor(
    private authManagerService: AuthManagerService,
    private devCycle: DevCycleService,
    private ngZone: NgZone,
  ) {}

  /**
   * Starts monitoring user activity for idle timeout.
   */
  async startMonitoring(): Promise<void> {
    const featureFlag = await this.devCycle.getVariable(
      'ew-1729-user-idle-time-before-logging-out',
      false,
    );

    if (!featureFlag) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.subscription = this.activityEvents$
        .pipe(
          debounceTime(300),
          switchMap(() => {
            return timer(this.idleTimeoutMs);
          }),
        )
        .subscribe(() => {
          this.ngZone.run(() => this.authManagerService.logout(true));
        });
    });
  }

  /**
   * Stops monitoring user activity.
   */
  stopMonitoring(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  /**
   * Manually reset timer by simulating activity.
   */
  resetTimer(): void {
    this.stopMonitoring();
    this.startMonitoring();
  }
}
